import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '@/App';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Users, AlertTriangle, FileText, CheckCircle2, 
  TrendingUp, Activity, BarChart3, ArrowLeft, GraduationCap,
  Settings, Save, RefreshCw, BookOpen, Mail, Shield, Clock, XCircle, Flame
} from 'lucide-react';
import { toast } from 'sonner';

const DivisionAnalytics = () => {
  const { divisionId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  
  // Safety Analytics state
  const [safetyAnalytics, setSafetyAnalytics] = useState(null);
  const [loadingSafety, setLoadingSafety] = useState(false);
  const [safetyTimeRange, setSafetyTimeRange] = useState('6months');
  
  // Training requirements state
  const [trainingRequirements, setTrainingRequirements] = useState([]);
  const [trainingProgress, setTrainingProgress] = useState(null);
  const [loadingTraining, setLoadingTraining] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  
  // Settings state
  const [settings, setSettings] = useState({
    incidentNotificationEmails: '',
    autoAssignJSAs: false,
    requireAuditApproval: false
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [divisionId]);

  // Load safety analytics when tab changes or time range changes
  useEffect(() => {
    if (activeTab === 'safety') {
      loadSafetyAnalytics();
    }
  }, [activeTab, safetyTimeRange, divisionId]);

  const loadAnalytics = async () => {
    try {
      const response = await axiosInstance.get(`/divisions/${divisionId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load division analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadSafetyAnalytics = async () => {
    try {
      setLoadingSafety(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/analytics/division/${divisionId}?time_range=${safetyTimeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch safety analytics');
      
      const data = await response.json();
      setSafetyAnalytics(data);
    } catch (error) {
      console.error('Failed to load safety analytics:', error);
      toast.error('Failed to load safety analytics');
    } finally {
      setLoadingSafety(false);
    }
  };

  // Body Map injury positions
  const INJURY_COLORS = {
    firstAid: '#22c55e',
    hospital: '#f59e0b',
    recordable: '#ef4444'
  };

  // Calculate injury positions from safety analytics data
  const getBodyMapInjuries = () => {
    if (!safetyAnalytics?.incidents?.byBodyPart) return [];
    
    const BODY_POSITIONS = {
      "head": { x: "29%", y: "14%", view: "front" },
      "shoulder": { x: "25%", y: "26%", view: "front" },
      "hand": { x: "21%", y: "48%", view: "front" },
      "finger": { x: "21%", y: "50%", view: "front" },
      "knee": { x: "31%", y: "64%", view: "front" },
      "foot": { x: "28%", y: "88%", view: "front" },
      "back": { x: "71%", y: "38%", view: "back" },
      "lower back": { x: "71%", y: "44%", view: "back" },
      "unknown": { x: "29%", y: "38%", view: "front" },
    };

    const injuries = [];
    let id = 1;
    Object.entries(safetyAnalytics.incidents.byBodyPart).forEach(([part, data]) => {
      const pos = BODY_POSITIONS[part.toLowerCase()] || BODY_POSITIONS["unknown"];
      const severity = data.types?.High || data.types?.Critical ? 'recordable' : 
                       data.types?.Medium ? 'hospital' : 'firstAid';
      injuries.push({
        id: id++,
        part,
        x: pos.x,
        y: pos.y,
        type: severity,
        count: data.count,
        description: `${data.count} injuries`
      });
    });
    return injuries;
  };

  const loadTrainingData = async () => {
    setLoadingTraining(true);
    try {
      const [reqResponse, progressResponse, coursesResponse] = await Promise.all([
        axiosInstance.get(`/divisions/${divisionId}/training-requirements`),
        axiosInstance.get(`/divisions/${divisionId}/training-progress`),
        axiosInstance.get('/courses')
      ]);
      setTrainingRequirements(reqResponse.data.requiredCourses || []);
      setTrainingProgress(progressResponse.data);
      setAvailableCourses(coursesResponse.data || []);
    } catch (error) {
      console.error('Failed to load training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setLoadingTraining(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get(`/divisions/${divisionId}`);
      const divisionData = response.data;
      setSettings({
        incidentNotificationEmails: divisionData.incidentNotificationEmails || '',
        autoAssignJSAs: divisionData.autoAssignJSAs || false,
        requireAuditApproval: divisionData.requireAuditApproval || false
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'training' && trainingRequirements.length === 0) {
      loadTrainingData();
    }
    if (tab === 'settings') {
      loadSettings();
    }
  };

  const toggleCourseRequirement = async (courseId) => {
    const isCurrentlyRequired = trainingRequirements.includes(courseId);
    const newRequirements = isCurrentlyRequired
      ? trainingRequirements.filter(id => id !== courseId)
      : [...trainingRequirements, courseId];
    
    try {
      await axiosInstance.put(`/divisions/${divisionId}/training-requirements`, {
        requiredCourses: newRequirements
      });
      setTrainingRequirements(newRequirements);
      toast.success(isCurrentlyRequired ? 'Course removed from requirements' : 'Course added to requirements');
    } catch (error) {
      console.error('Failed to update training requirements:', error);
      toast.error('Failed to update training requirements');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axiosInstance.put(`/divisions/${divisionId}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center text-gray-500 py-8">
        No analytics data available
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Divisions
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {analytics.divisionName}
        </h1>
        <p className="text-gray-600 mt-1">Division management and analytics</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="safety" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Safety
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4" />
            Training
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          {/* Key Metrics Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Workers */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate(`/admin/users?divisionId=${divisionId}`)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Workers</p>
                <p className="text-3xl font-bold text-blue-600">
                  {analytics.workers.total}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.workers.active} active
                </p>
              </div>
              <Users className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        {/* Incidents */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate(`/admin/incidents?divisionId=${divisionId}`)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Incidents</p>
                <p className="text-3xl font-bold text-red-600">
                  {analytics.incidents.total}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.incidents.open} open
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </CardContent>
        </Card>

        {/* JSAs */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate(`/jsa?divisionId=${divisionId}`)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">JSAs Completed</p>
                <p className="text-3xl font-bold text-green-600">
                  {analytics.jsas.submitted}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.jsas.completionRate}% completion
                </p>
              </div>
              <FileText className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        {/* Audits */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate(`/admin/audits?divisionId=${divisionId}`)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Audits Completed</p>
                <p className="text-3xl font-bold text-purple-600">
                  {analytics.audits.completed}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {analytics.audits.avgComplianceScore}% avg score
                </p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Incidents Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Incidents by Severity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(analytics.incidents.bySeverity).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.incidents.bySeverity).map(([severity, count]) => (
                  <div key={severity} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        severity === 'Critical' ? 'bg-red-600' :
                        severity === 'High' ? 'bg-orange-500' :
                        severity === 'Medium' ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`} />
                      <span className="text-sm font-medium">{severity}</span>
                    </div>
                    <span className="text-lg font-bold">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No incidents recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Incidents by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Incidents by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(analytics.incidents.byType).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(analytics.incidents.byType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize">
                      {type.replace('_', ' ')}
                    </span>
                    <span className="text-lg font-bold">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No incidents recorded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Activity (Last 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Incidents Reported</p>
              <p className="text-2xl font-bold text-red-600">
                {analytics.incidents.recent30Days}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">JSAs Created</p>
              <p className="text-2xl font-bold text-green-600">
                {analytics.jsas.recent30Days}
              </p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">Audits Conducted</p>
              <p className="text-2xl font-bold text-purple-600">
                {analytics.audits.recent30Days}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Training Progress */}
      {analytics.training && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Training Completion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-600">Overall Completion Rate</p>
                <p className="text-3xl font-bold text-blue-600">
                  {analytics.training.completionRate}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Courses Completed</p>
                <p className="text-lg font-semibold">
                  {analytics.training.completed} / {analytics.training.totalRequired}
                </p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all"
                style={{ width: `${analytics.training.completionRate}%` }}
              />
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* Safety Tab */}
        <TabsContent value="safety">
          <div className="space-y-6">
            {/* Time Range Selector */}
            <div className="flex justify-end gap-2">
              <Button variant={safetyTimeRange === '30days' ? 'default' : 'outline'} size="sm" onClick={() => setSafetyTimeRange('30days')}>30 Days</Button>
              <Button variant={safetyTimeRange === '6months' ? 'default' : 'outline'} size="sm" onClick={() => setSafetyTimeRange('6months')}>6 Months</Button>
              <Button variant={safetyTimeRange === '1year' ? 'default' : 'outline'} size="sm" onClick={() => setSafetyTimeRange('1year')}>1 Year</Button>
            </div>

            {loadingSafety ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading safety analytics...</span>
              </div>
            ) : safetyAnalytics ? (
              <>
                {/* Safety Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="p-5">
                      <p className="text-green-100 text-sm">Total JSAs</p>
                      <p className="text-3xl font-bold mt-1">{safetyAnalytics.jsas?.total || 0}</p>
                      <p className="text-green-200 text-xs mt-1">{safetyAnalytics.jsas?.active || 0} active</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="p-5">
                      <p className="text-blue-100 text-sm">Total Audits</p>
                      <p className="text-3xl font-bold mt-1">{safetyAnalytics.audits?.total || 0}</p>
                      <p className="text-blue-200 text-xs mt-1">Avg score: {safetyAnalytics.audits?.averageScore || 'N/A'}%</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
                    <CardContent className="p-5">
                      <p className="text-yellow-100 text-sm">Total Incidents</p>
                      <p className="text-3xl font-bold mt-1">{safetyAnalytics.incidents?.total || 0}</p>
                      <p className="text-yellow-200 text-xs mt-1">This period</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="p-5">
                      <p className="text-red-100 text-sm">Near Misses</p>
                      <p className="text-3xl font-bold mt-1">{safetyAnalytics.nearMisses?.total || 0}</p>
                      <p className="text-red-200 text-xs mt-1">Reported</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-5">
                      <p className="text-purple-100 text-sm">Days Since Recordable</p>
                      <p className="text-3xl font-bold mt-1">{safetyAnalytics.incidents?.daysSinceRecordable ?? 'N/A'}</p>
                      <p className="text-purple-200 text-xs mt-1">Safety milestone</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Metrics */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* JSA Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        JSA Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{safetyAnalytics.jsas?.active || 0}</div>
                          <div className="text-sm text-gray-600">Active</div>
                        </div>
                        <div className="text-center p-4 bg-yellow-50 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">{safetyAnalytics.jsas?.draft || 0}</div>
                          <div className="text-sm text-gray-600">Draft</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">{safetyAnalytics.jsas?.archived || 0}</div>
                          <div className="text-sm text-gray-600">Archived</div>
                        </div>
                      </div>
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-gray-600">Avg Hazards per JSA</span>
                        <span className="font-bold text-blue-600">{safetyAnalytics.jsas?.averageHazards || 0}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Incident Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Incident Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span>First Aid</span>
                          </div>
                          <span className="font-bold text-green-600">{safetyAnalytics.incidents?.bySeverity?.Low || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <span>Medical Treatment</span>
                          </div>
                          <span className="font-bold text-yellow-600">{safetyAnalytics.incidents?.bySeverity?.Medium || 0}</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <span>Recordable</span>
                          </div>
                          <span className="font-bold text-red-600">{(safetyAnalytics.incidents?.bySeverity?.High || 0) + (safetyAnalytics.incidents?.bySeverity?.Critical || 0)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Activity */}
                {safetyAnalytics.incidents?.recentIncidents?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Incidents</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-4">Date</th>
                            <th className="text-left py-2 px-4">Type</th>
                            <th className="text-left py-2 px-4">Location</th>
                            <th className="text-left py-2 px-4">Severity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safetyAnalytics.incidents.recentIncidents.slice(0, 5).map((incident) => (
                            <tr key={incident.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-4">{incident.date}</td>
                              <td className="py-2 px-4">{incident.type}</td>
                              <td className="py-2 px-4">{incident.location}</td>
                              <td className="py-2 px-4">
                                <Badge variant={incident.severity === 'High' || incident.severity === 'Critical' ? 'destructive' : incident.severity === 'Medium' ? 'default' : 'secondary'}>
                                  {incident.severity}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}

                {/* Body Map */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Injury Body Map
                    </CardTitle>
                    <CardDescription>Injury locations for this division</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col lg:flex-row gap-6 items-start">
                      {/* Body silhouette */}
                      <div className="relative w-full max-w-[400px] h-[360px] mx-auto bg-white rounded-lg">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_formbuilder-7/artifacts/dj7vgm3e_injury%20silouette.png" 
                          alt="Human body silhouette"
                          className="w-full h-full object-contain pointer-events-none"
                          style={{ filter: 'contrast(1.1)', mixBlendMode: 'multiply' }}
                        />
                        
                        {/* Injury Dots */}
                        {getBodyMapInjuries().map((injury) => (
                          <div
                            key={injury.id}
                            className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-all hover:scale-150 z-10"
                            style={{ left: injury.x, top: injury.y }}
                            title={`${injury.part}: ${injury.description}`}
                          >
                            <div 
                              className="w-4 h-4 rounded-full border-2 border-white shadow-lg"
                              style={{ 
                                backgroundColor: INJURY_COLORS[injury.type],
                                boxShadow: `0 0 8px ${INJURY_COLORS[injury.type]}`
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      
                      {/* Legend */}
                      <div className="lg:w-48 space-y-3">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-semibold mb-2">Injury Severity</h4>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              <span className="text-sm">First Aid</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                              <span className="text-sm">Medical Treatment</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                              <span className="text-sm">Recordable</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Summary */}
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <h4 className="text-sm font-semibold mb-2">Summary</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">First Aid:</span>
                              <span className="font-bold text-green-600">{safetyAnalytics.incidents?.bySeverity?.Low || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Medical:</span>
                              <span className="font-bold text-yellow-600">{safetyAnalytics.incidents?.bySeverity?.Medium || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Recordable:</span>
                              <span className="font-bold text-red-600">{(safetyAnalytics.incidents?.bySeverity?.High || 0) + (safetyAnalytics.incidents?.bySeverity?.Critical || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No safety data available for this division
              </div>
            )}
          </div>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training">
          <div className="space-y-6">
            {/* Training Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  Training Requirements
                </CardTitle>
                <CardDescription>
                  Configure which courses are mandatory for workers in this division
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTraining ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                    <span className="text-gray-500">Loading courses...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {availableCourses.length > 0 ? (
                      availableCourses.map((course) => (
                        <div 
                          key={course.id} 
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-blue-500" />
                            <div>
                              <p className="font-medium">{course.title}</p>
                              <p className="text-sm text-gray-500">
                                {course.duration || 'Duration varies'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={trainingRequirements.includes(course.id)}
                            onCheckedChange={() => toggleCourseRequirement(course.id)}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No courses available</p>
                        <p className="text-sm">Add courses in the Training section first</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Training Progress by Worker */}
            {trainingProgress && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Worker Training Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {trainingProgress.workers && trainingProgress.workers.length > 0 ? (
                    <div className="space-y-3">
                      {trainingProgress.workers.map((worker) => (
                        <div key={worker.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{worker.name}</p>
                            <p className="text-sm text-gray-500">{worker.email}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-32 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  worker.completionRate >= 100 ? 'bg-green-500' :
                                  worker.completionRate >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(worker.completionRate, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-12 text-right">
                              {worker.completionRate}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No workers in this division</p>
                  )}
                </CardContent>
              </Card>
            )}

            <Button onClick={loadTrainingData} variant="outline" className="w-full gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh Training Data
            </Button>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>
                  Configure email notifications for this division
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="notificationEmails">Incident Notification Emails</Label>
                  <Input
                    id="notificationEmails"
                    placeholder="email1@company.com, email2@company.com"
                    value={settings.incidentNotificationEmails}
                    onChange={(e) => setSettings({ ...settings, incidentNotificationEmails: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated list of emails to notify when incidents are reported
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Safety Settings
                </CardTitle>
                <CardDescription>
                  Configure safety and compliance settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Auto-Assign JSAs</p>
                    <p className="text-sm text-gray-500">
                      Automatically assign relevant JSAs to workers based on their craft
                    </p>
                  </div>
                  <Switch
                    checked={settings.autoAssignJSAs}
                    onCheckedChange={(checked) => setSettings({ ...settings, autoAssignJSAs: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Require Audit Approval</p>
                    <p className="text-sm text-gray-500">
                      Audits must be approved by a division admin before closing
                    </p>
                  </div>
                  <Switch
                    checked={settings.requireAuditApproval}
                    onCheckedChange={(checked) => setSettings({ ...settings, requireAuditApproval: checked })}
                  />
                </div>
              </CardContent>
            </Card>

            <Button 
              onClick={saveSettings} 
              disabled={savingSettings}
              className="w-full gap-2"
            >
              {savingSettings ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DivisionAnalytics;
